import * as THREE from 'three';
import type { SketchFace, SketchSolid, SketchVec3, SketchLoopMeta } from './types';
import { facePlane } from './types';
import { regenerateLoop } from './curves';

/**
 * Analytic prism factory — the solid primitive behind Push/Pull.
 *
 * A prism from face loops + a signed extrusion distance along the face
 * normal: base loops stay on the original plane, top loops are lifted, one
 * side quad per contour edge. Output is a fully analytic closed shell (no
 * triangulation involved), so the result keeps feeding the face tools.
 */

const v3 = (p: SketchVec3): THREE.Vector3 => new THREE.Vector3(...p);
const asTuple = (p: THREE.Vector3): SketchVec3 => [p.x, p.y, p.z];

export interface PrismBuild {
    faces: SketchFace[];
    /** Raised top loops (for tools that need the new plane). */
    topOrigin: THREE.Vector3;
    /** Signed distance along the face normal. */
    distance: number;
}

/**
 * Build a closed prism from the OPEN loops of a face (closing segments
 * implied). The prism's outward orientation: base faces keep the source
 * normal (pointing AWAY from the prism body when distance is along +normal),
 * top faces flip, side faces follow the loop winding.
 */
export function buildPrism(
    faceIdPrefix: string,
    outer: SketchVec3[],
    holes: SketchVec3[][],
    origin: THREE.Vector3,
    normal: THREE.Vector3,
    distance: number,
    materialId?: string,
    outerMeta?: SketchLoopMeta
): PrismBuild {
    const n = normal.clone().normalize();
    const top = origin.clone().addScaledVector(n, distance);
    const lift = (p: SketchVec3): SketchVec3 =>
        asTuple(v3(p).addScaledVector(n, distance));

    const uuid = () => Math.random().toString(36).slice(2, 10);

    // Base face: same plane as the source face. Its outward normal points
    // opposite the extrusion direction (the prism grows along +n).
    const baseNormal = distance >= 0 ? n.clone().negate() : n.clone();
    const topNormal = distance >= 0 ? n.clone() : n.clone().negate();

    // Exact curve regen: when the source loop is a circle/arc/polygon the
    // top loop is RE-SAMPLED from its definition at the lifted plane, not
    // copied from the base tessellation.
    const topOuter = outerMeta ? regenerateLoop(outerMeta, lift) : outer.map(lift);

    const faces: SketchFace[] = [
        {
            id: `${faceIdPrefix}-base-${uuid()}`,
            origin: asTuple(origin),
            normal: asTuple(baseNormal),
            outer,
            holes,
            loopMeta: outerMeta,
        },
        {
            id: `${faceIdPrefix}-top-${uuid()}`,
            origin: asTuple(top),
            normal: asTuple(topNormal),
            outer: topOuter,
            holes: holes.map((h) => h.map(lift)),
            materialId,
            loopMeta: outerMeta,
        },
    ];

    // Side quads: for each open contour edge (i -> i+1), the wall between
    // base and top. Winding: (a, b, b+d, a+d) gives outward normals when
    // the loop is CCW seen from +n — verified by watertightness later.
    const wall = (loop: SketchVec3[]): void => {
        for (let i = 0; i < loop.length; i++) {
            const a = v3(loop[i]);
            const b = v3(loop[(i + 1) % loop.length]);
            faces.push({
                id: `${faceIdPrefix}-side-${i}-${uuid()}`,
                origin: asTuple(a),
                normal: asTuple(
                    new THREE.Vector3().subVectors(b, a).cross(n).normalize()
                ),
                outer: [
                    asTuple(a),
                    asTuple(b),
                    asTuple(b.clone().addScaledVector(n, distance)),
                    asTuple(a.clone().addScaledVector(n, distance)),
                ],
                holes: [],
                materialId,
            });
        }
    };
    wall(outer);
    holes.forEach((h) => wall(h));

    return { faces, topOrigin: top, distance };
}

/** Triangulated world-space geometry of one solid (merged face meshes). */
export function solidToGeometry(solid: SketchSolid): THREE.BufferGeometry {
    const positions: number[] = [];
    const normals: number[] = [];
    for (const face of solid.faces) {
        // Re-triangulate per face by lifting the loop into a temporary face
        // and letting ShapeUtils do the earcut; positions go flat (non-indexed).
        const geo = faceGeometryPositions(face);
        positions.push(...geo.positions);
        normals.push(...geo.normals);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
    geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(normals), 3));
    geometry.computeBoundingSphere();
    return geometry;
}

/** Analytic world-space bounding box of a solid (from its face loops). */
export function solidBounds(solid: SketchSolid): THREE.Box3 {
    const box = new THREE.Box3();
    const p = new THREE.Vector3();
    for (const face of solid.faces) {
        for (const loop of [face.outer, ...face.holes]) {
            for (const point of loop) box.expandByPoint(p.set(...point));
        }
    }
    return box;
}

/**
 * Boolean tolerance pad: a fraction of the solid's diagonal, clamped to a
 * band that beats float noise (1e-5 ≈ SketchUp's own 1/1000" tolerance)
 * without visibly moving geometry. Boolean tools padded by this never touch
 * their operands EXACTLY coplanar — the one configuration three-bvh-csg's
 * 1e-10 tolerances provably break on.
 */
export function toolEpsilon(solid: SketchSolid): number {
    const box = solidBounds(solid);
    if (!Number.isFinite(box.min.x)) return 1e-5;
    const diag = box.getSize(new THREE.Vector3()).length();
    return Math.min(1e-3, Math.max(1e-5, diag * 1e-6));
}

// Local import-free triangulation of a face into flat position/normal arrays
// (mirrors triangulate.faceToGeometry without the BufferGeometry wrapper).
function faceGeometryPositions(face: SketchFace): { positions: number[]; normals: number[] } {
    const positions: number[] = [];
    const normals: number[] = [];
    const plane = facePlane(face);
    const n = new THREE.Vector3(...face.normal).normalize();
    const origin = new THREE.Vector3(...face.origin);
    const ref = Math.abs(n.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
    const u = new THREE.Vector3().crossVectors(ref, n).normalize();
    const v = new THREE.Vector3().crossVectors(n, u).normalize();
    const project = (loop: SketchVec3[]): THREE.Vector2[] =>
        loop.map((p) => {
            const d = v3(p).sub(origin);
            return new THREE.Vector2(d.dot(u), d.dot(v));
        });

    const contour = project(face.outer);
    const holes = face.holes.filter((h) => h.length >= 3).map(project);
    let triangles: number[][];
    try {
        triangles = THREE.ShapeUtils.triangulateShape(contour, holes);
    } catch {
        // Degenerate contours (self-intersections from a repaired mesh):
        // fall back to an ear fan of the outer contour so the face stays
        // VISIBLE instead of vanishing from the shell — a slightly wrong
        // patch beats a hole in the solid.
        triangles = [];
        for (let i = 1; i < face.outer.length - 1; i++) triangles.push([0, i, i + 1]);
    }
    const pool = [face.outer, ...face.holes.filter((h) => h.length >= 3)];
    for (const tri of triangles) {
        for (const idx of tri) {
            let remaining = idx;
            let loop = 0;
            while (loop < pool.length && remaining >= pool[loop].length) {
                remaining -= pool[loop].length;
                loop++;
            }
            const p = pool[loop]?.[remaining] ?? face.outer[0];
            positions.push(p[0], p[1], p[2]);
            normals.push(n.x, n.y, n.z);
        }
    }
    void plane;
    return { positions, normals };
}
